import { useEffect, useState } from "react";
import EmailInput from "../../common/components/EmailInput";
import {
  cleanPersonNamePreservingOverLimit,
  isAutofilledNameLocked,
  isPersonNameOverLimit,
  personNameInputMaxLength,
} from "../../common/utils";
import { SelectInput, FormField, TextInput } from "./AnnuitantTab";

const personalFields = ({
  prefix,
  errors,
  salutationOptions,
  MARITAL_STATUS_OPTIONS,
  EDUCATION_OPTIONS,
  cleanNameInput,
  isNameDisabled,
}) => [
  {
    label: "Title",
    name: `${prefix}Title`,
    type: "select",
    options: salutationOptions,
    placeholder: "Select",
    error: errors?.[`${prefix}Title`],
  },
  {
    label: "Full Name",
    name: `${prefix}FullName`,
    type: "text",
    formatter: cleanNameInput,
    maxLength: 30,
    disabled: isNameDisabled,
    error: errors?.[`${prefix}FullName`],
  },
  {
    label: "Father's Name",
    name: `${prefix}FatherName`,
    type: "text",
    formatter: cleanNameInput,
    maxLength: 20,
    error: errors?.[`${prefix}FatherName`],
  },
  {
    label: "Mother's Name",
    name: `${prefix}MotherName`,
    type: "text",
    formatter: cleanNameInput,
    maxLength: 20,
    error: errors?.[`${prefix}MotherName`],
  },
  {
    label: "Email",
    name: `${prefix}Email`,
    type: "email",
    placeholder: "Enter email",
    maxLength: 50,
    error: errors?.[`${prefix}Email`],
  },
  {
    label: "Marital Status",
    name: `${prefix}MaritalStatus`,
    type: "select",
    options: MARITAL_STATUS_OPTIONS,
    placeholder: "Select",
    error: errors?.[`${prefix}MaritalStatus`],
  },
  {
    label: "PAN Card Number",
    name: `${prefix}PanNumber`,
    type: "pan",
    placeholder: "e.g. ABCDE1234F",
    maxLength: 10,
    error: errors?.[`${prefix}PanNumber`],
  },
  {
    label: "Education",
    name: `${prefix}Education`,
    type: "select",
    options: EDUCATION_OPTIONS,
    placeholder: "Select",
    error: errors?.[`${prefix}Education`],
  },
  {
    label: "Annual Income",
    name: `${prefix}AnnualIncome`,
    type: "number",
    placeholder: "e.g. 50,000 per year",
    maxLength: 15,
    error: errors?.[`${prefix}AnnualIncome`],
  },
  {
    label: "Job Description",
    name: `${prefix}JobDescription`,
    type: "text",
    maxLength: 20,
    error: errors?.[`${prefix}JobDescription`],
  },
];

export const DynamicFormFields = ({
  prefix,
  formData,
  errors,
  onChange,
  salutationOptions,
  MARITAL_STATUS_OPTIONS,
  EDUCATION_OPTIONS,
  cleanNameInput,
  name,
  isNameDisabled,
  isFatherFromAadhaar = false,
}) => {
  const fullNameKey = `${prefix}FullName`;
  const fatherNameKey = `${prefix}FatherName`;
  const [nameEditUnlocked, setNameEditUnlocked] = useState(false);
  const [fatherEditUnlocked, setFatherEditUnlocked] = useState(false);

  useEffect(() => {
    if (!name) return;

    // Only prefill if the field is currently empty
    if (!formData?.[fullNameKey]) {
      onChange(fullNameKey, name);
    }
  }, [prefix, name, formData, onChange, fullNameKey]);

  useEffect(() => {
    if (isPersonNameOverLimit(formData?.[fullNameKey])) {
      setNameEditUnlocked(true);
    }
  }, [formData, fullNameKey]);

  useEffect(() => {
    if (isPersonNameOverLimit(formData?.[fatherNameKey])) {
      setFatherEditUnlocked(true);
    }
  }, [formData, fatherNameKey]);

  const fields = personalFields({
    prefix,
    errors,
    salutationOptions,
    MARITAL_STATUS_OPTIONS,
    EDUCATION_OPTIONS,
    cleanNameInput,
    isNameDisabled,
  });

  return (
    <div className="form-wraper">
      {fields.map((field) => (
        <FormField
          key={field.name}
          label={field.label}
          halfWidth
          error={field.error}
        >
          {field.type === "select" ? (
            <SelectInput
              name={field.name}
              value={formData?.[field.name]}
              onChange={onChange}
              options={field.options}
              placeholder={field.placeholder}
            />
          ) : field.type === "email" ? (
            <div className="proposal-autocomplete-wrapper">
              <EmailInput
                name={field.name}
                value={formData?.[field.name] || ""}
                onChange={onChange}
                placeholder={field.placeholder}
                maxLength={field.maxLength}
              />
            </div>
          ) : (
            <TextInput
              name={field.name}
              value={formData?.[field.name] || ""}
              placeholder={field.placeholder}
              maxLength={
                field.name.endsWith("FullName") ||
                (field.name.endsWith("FatherName") && isFatherFromAadhaar)
                  ? personNameInputMaxLength(
                      formData?.[field.name],
                      field.maxLength,
                    )
                  : field.maxLength
              }
              disabled={
                field.name.endsWith("FullName") && isNameDisabled
                  ? isAutofilledNameLocked(
                      true,
                      formData?.[field.name],
                      nameEditUnlocked,
                    )
                  : field.name.endsWith("FatherName") && isFatherFromAadhaar
                    ? isAutofilledNameLocked(
                        true,
                        formData?.[field.name],
                        fatherEditUnlocked,
                      )
                    : field.disabled
              }
              autoComplete="off"
              inputMode={
                field.type === "number"
                  ? "numeric"
                  : undefined
              }
              onChange={(inputName, value) => {
                let formattedValue = value;
                const preserveOverLimit =
                  inputName.endsWith("FullName") ||
                  (inputName.endsWith("FatherName") && isFatherFromAadhaar);
                if (field.type === "pan") {
                  formattedValue = value
                    .toUpperCase()
                    .replace(/[^A-Z0-9]/g, "")
                    .slice(0, 10);
                } else if (field.type === "number") {
                  formattedValue = value.replace(/\D/g, "");
                } else if (preserveOverLimit) {
                  formattedValue = cleanPersonNamePreservingOverLimit(
                    value,
                    undefined,
                    formData?.[inputName],
                  );
                } else if (field.formatter) {
                  formattedValue = field.formatter(value);
                }
                onChange(inputName, formattedValue);
              }}
            />
          )}
        </FormField>
      ))}
    </div>
  );
};

/* =========================
   ADDRESS FIELD CONFIG
========================= */

const addressFields = ({
  prefix,
  errors,
  stateOptions,
  filteredCities,
  handleStateChange,
  handleCityChange,
  formData,
}) => [
  {
    label: "Address Line 1",
    name: `${prefix}AddressLine1`,
    type: "text",
    maxLength: 30,
    error: errors?.[`${prefix}AddressLine1`],
  },
  {
    label: "Address Line 2",
    name: `${prefix}AddressLine2`,
    type: "text",
    maxLength: 30,
    error: errors?.[`${prefix}AddressLine2`],
  },
  {
    label: "Address Line 3",
    name: `${prefix}AddressLine3`,
    type: "text",
    optional: true,
    maxLength: 30,
  },
  {
    label: "Pin Code",
    name: `${prefix}PinCode`,
    type: "pincode",
    maxLength: 6,
    error: errors?.[`${prefix}PinCode`],
  },
  {
    label: "State",
    name: `${prefix}State`,
    type: "select",
    options: stateOptions,
    placeholder: "Select",
    onChange: handleStateChange,
    error: errors?.[`${prefix}State`],
  },
  {
    label: "City",
    name: `${prefix}City`,
    type: "select",
    options: filteredCities,
    placeholder: "Select",
    onChange: handleCityChange,
    error: errors?.[`${prefix}City`],
    disabled: !formData?.[`${prefix}State`],
  },
];

/* =========================
   ADDRESS COMPONENT
========================= */

export const DynamicAddressFields = ({
  prefix,
  formData,
  errors,
  onChange,
  stateOptions,
  filteredCities,
  handleStateChange,
  handleCityChange,
}) => {
  const fields = addressFields({
    prefix,
    errors,
    stateOptions,
    filteredCities,
    handleStateChange,
    formData,
    handleCityChange,
  });

  return (
    <div className="form-wraper">
      {fields.map((field) => (
        <FormField
          key={field.name}
          label={field.label}
          halfWidth
          optional={field.optional}
          error={field.error}
        >
          {field.type === "select" ? (
            <SelectInput
              name={field.name}
              value={formData?.[field.name]}
              onChange={field.onChange || onChange}
              options={field.options}
              placeholder={field.placeholder}
              disabled={field.disabled}
            />
          ) : (
            <TextInput
              name={field.name}
              value={formData?.[field.name] || ""}
              maxLength={field.maxLength}
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              onChange={(name, value) => {
                let formattedValue = value;

                if (field.type === "pincode") {
                  formattedValue = value.replace(/\D/g, "").slice(0, 6);
                }

                onChange(name, formattedValue);
              }}
            />
          )}
        </FormField>
      ))}
    </div>
  );
};

/* =========================
   BANK FIELD CONFIG
========================= */

const bankFields = ({ prefix, errors }) => [
  {
    label: "Name of Bank",
    name: `${prefix}BankName`,
    type: "bankAutocomplete",
    error: errors?.[`${prefix}BankName`],
    maxLength: 30,
  },
  {
    label: "Branch Name",
    name: `${prefix}BranchName`,
    type: "text",
    error: errors?.[`${prefix}BranchName`],
    maxLength: 30,
  },
  {
    label: "Account Number",
    name: `${prefix}AccountNumber`,
    type: "accountNumber",
    error: errors?.[`${prefix}AccountNumber`],
    maxLength: 18,
  },
  {
    label: "IFSC Code",
    name: `${prefix}IfscCode`,
    type: "ifsc",
    maxLength: 11,
    placeholder: "e.g. HDFC0001234",
    error: errors?.[`${prefix}IfscCode`],
  },
];

/* =========================
   BANK COMPONENT
========================= */

export const DynamicBankFields = ({
  prefix,
  formData,
  errors,
  onChange,
  bankSearch,
  setBankSearch,
  showBankDropdown,
  setShowBankDropdown,
  filteredBanks,
}) => {
  const fields = bankFields({
    prefix,
    errors,
  });

  return (
    <div className="form-wraper">
      {fields.map((field) => (
        <FormField
          key={field.name}
          label={field.label}
          halfWidth
          error={field.error}
        >
          {field.type === "bankAutocomplete" ? (
            <div className="proposal-autocomplete-wrapper">
              <TextInput
                name={field.name}
                value={formData?.[field.name] || ""}
                autoComplete="off"
                placeholder="Search bank"
                maxLength={field.maxLength || 30}
                onChange={(name, value) => {
                  const limited = String(value || "").slice(0, field.maxLength || 30);
                  setBankSearch(limited);
                  onChange(name, limited);
                  setShowBankDropdown(limited.trim().length > 0);
                }}
                onFocus={() => {
                  if (bankSearch.trim().length > 0) {
                    setShowBankDropdown(true);
                  }
                }}
                onBlur={() => setShowBankDropdown(false)}
              />

              {showBankDropdown && filteredBanks.length > 0 && (
                <div className="proposal-autocomplete-dropdown">
                  {filteredBanks.slice(0, 10).map((bank) => (
                    <div
                      key={bank.value}
                      className="proposal-autocomplete-option"
                      onMouseDown={() => {
                        const limited = String(bank.label || "").slice(
                          0,
                          field.maxLength || 30,
                        );
                        setBankSearch(limited);
                        onChange(field.name, limited);
                        setShowBankDropdown(false);
                      }}
                    >
                      {bank.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <TextInput
              name={field.name}
              value={formData?.[field.name] || ""}
              autoComplete="off"
              placeholder={field.placeholder}
              maxLength={field.maxLength}
              onChange={(name, value) => {
                let formattedValue = value;

                if (field.type === "ifsc") {
                  formattedValue = value.toUpperCase().trim();

                  if (!/^[A-Z0-9]*$/.test(formattedValue)) {
                    return;
                  }
                }

                if (field.type === "accountNumber") {
                  formattedValue = String(value || "")
                    .replace(/\D/g, "")
                    .slice(0, field.maxLength || 18);
                }

                onChange(name, formattedValue);
              }}
            />
          )}
        </FormField>
      ))}
    </div>
  );
};
